import React, { useEffect, useState } from "react";
import "./MyWorkspace.scss";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import {
  resetWorkspaceListState,
  fetchWorkspaceListData,
} from "../../store/slices/WorkspaceListSlice";
import {
  resetSyncOrderState,
  syncOrder,
} from "../../store/slices/SyncOrderSlice";
import {
  MotifProgressLoader,
  MotifButton,
  MotifTabNavigation,
  MotifTabControl,
  MotifDropdown,
  MotifDropdownItem,
  MotifTruncate,
  MotifCheckbox,
  MotifErrorMessage,
  MotifToast,
} from "@ey-xd/motif-react";
import { MotifIcon } from "@ey-xd/motif-react";
import {
  IconoirNavArrowUp,
  IconoirNavArrowDown,
  IconoirFilterList,
  IconoirEditPencil,
  IconoirTrash,
  IconoirShareIos,
} from "@ey-xd/motif-icon";
import { useNavigate } from "react-router";
import productListEmptyImg from "../../assets/images/empty_product_list_default.svg";
import OrdersTable from "./OrderTable/OrderTable";
import MyOrders from "./MyOrders/MyOrders";
import MyEstimates from "./MyEstimates/MyEstimates";
import {
  getFormattedDate,
  getTotalOrderAmount,
  onViewOrderItems,
  getArrowDirection,
} from "./helper";
import MyDrafts from "./MyDrafts/MyDrafts";
import { containsEmoji, setOrderId } from "../../utils/common";
import DeleteOrderModal from "./DeleteOrderModal/DeleteOrderModal";
import SearchInput from "../SearchInput/SearchInput";
import NoSearchResult from "../NoSearchResult/NoSearchResult";
import { EMOJI_ERROR_MESSAGE, SEARCH_TIMER } from "../../Constant";
import ErrorOrderPage from "../ErrorOrderPage/ErrorOrderPage";
import ShareEstimateModal from "../ShareEstimateModal/ShareEstimateModal";

interface MyWorkspaceProps {
  goBackToCTTab: () => void;
  handleBreadcrumb: any;
  defaultTab: number;
}

const MyWorkspace: React.FC<MyWorkspaceProps> = ({
  goBackToCTTab,
  defaultTab,
  handleBreadcrumb,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  interface Order {
    OrderParentId: string;
    Description: string;
    CreatedDtm: string;
    StatusCd: string;
  }

  interface FilterOptions {
    all: boolean;
    active: boolean;
    estimate: boolean;
    draft: boolean;
  }

  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [activeOrderData, setActiveOrderData] = useState<any[]>([]);
  const [estimatedOrderData, setEstimatedOrderData] = useState<any[]>([]);
  const [draftOrderData, setDraftOrderData] = useState<Order[]>([]);
  const [showDeleteOrderModel, setShowDeleteOrderModel] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<number>(0);
  const [isLoadingActive, setIsLoadingActive] = useState(false);
  const [isLoadingEstimate, setIsLoadingEstimate] = useState(false);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
  const [isActiveError, setIsActiveError] = useState(false);
  const [isEstimateError, setIsEstimateError] = useState(false);
  const [isDraftError, setIsDraftError] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentSharedOrder, setCurrentSharedOrder] = useState<Order>({
    OrderParentId: "",
    Description: "",
    CreatedDtm: "",
    StatusCd: "",
  });
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastVariant, setToastVariant] = useState<"success" | "error">(
    "success",
  );
  const [toggleFilterState, setToggleFilterState] = useState<boolean>(false);
  const [orderDetails, setOrderDetails] = useState<
    {
      orderParentId: string;
      isExpanded: boolean;
    }[]
  >([]);

  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [selectedFiltersValues, setSelectedFiltersValues] = useState<string[]>(
    [],
  );

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    all: false,
    active: false,
    estimate: false,
    draft: false,
  });

  const [searchValidationError, setSearchValidationError] =
    useState<string>("");

  type FilterOption = "all" | "active" | "estimate" | "draft";

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage("");
        setToastVariant("success");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const handleFilterChange = (index: FilterOption) => {
    setFilterOptions((prev) => {
      if (index === "all") {
        const newValue = !prev.all;
        return {
          all: newValue,
          active: newValue,
          estimate: newValue,
          draft: newValue,
        };
      } else {
        const updated = {
          ...prev,
          [index]: !prev[index],
          all: false,
        };
        return updated;
      }
    });
  };

  const fetchOrders = async (status?: string) => {
    resetWorkspaceListState();

    if (!status) {
      setIsLoading(true);
      setIsError(false);
    }
    if (status === "active") {
      setIsLoadingActive(true);
      setIsActiveError(false);
    }
    if (status === "estimate") {
      setIsLoadingEstimate(true);
      setIsEstimateError(false);
    }
    if (status === "draft") {
      setIsLoadingDrafts(true);
      setIsDraftError(false);
    }

    const selectedFilters = Object.keys(filterOptions).filter(
      (key) => filterOptions[key as keyof FilterOptions],
    );

    const params: any = {
      Action: "retrieveAllOrder",
      ...(status && { Status: status }),
      ...(debouncedSearchValue && { Search: debouncedSearchValue }),
    };
    if (!status && selectedFilters.length > 0) {
      params.Status = selectedFilters;
    }

    const action = await dispatch(fetchWorkspaceListData(params));
    const data = action.payload?.Data || [];
    const error = action.payload?.message || null;
    switch (status) {
      case "active":
        setActiveOrderData(data);
        setIsLoadingActive(false);
        setIsActiveError(!!error);
        break;
      case "estimate":
        setEstimatedOrderData(data);
        setIsLoadingEstimate(false);
        setIsEstimateError(!!error);
        break;
      case "draft":
        setDraftOrderData(data);
        setIsLoadingDrafts(false);
        setIsDraftError(!!error);
        break;
      default:
        setAllOrders(data);
        setIsLoading(false);
        setIsError(!!error);
        break;
    }
  };

  // This function to validate search and prevent API call if emoji exists
  const shouldSkipApiCall = (searchText: string): boolean => {
    const hasEmoji = containsEmoji(searchText);
    if (hasEmoji) {
      return true;
    }
    return false;
  };

  const fetchMyWorkspaceData = () => {
    switch (selectedTab) {
      case 0:
        fetchOrders();
        break;
      case 1:
        fetchOrders("active");
        break;
      case 2:
        fetchOrders("estimate");
        break;
      case 3:
        fetchOrders("draft");
        break;
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, SEARCH_TIMER);

    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    if (shouldSkipApiCall(debouncedSearchValue)) {
      return;
    }
    fetchMyWorkspaceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setSelectedFiltersValues(getSelectedFilterLabels);
  }, [selectedTab, debouncedSearchValue, filterOptions]);

  useEffect(() => {
    setSelectedTab(defaultTab);
    handleSearch("");
    setSearchValue("");
    setSearchValidationError("");
    setDebouncedSearchValue("");
  }, [defaultTab]);

  useEffect(() => {
    if (allOrders.length > 0 && selectedTab === 0) {
      const newOrderDetails = allOrders.map((order: any) => ({
        orderParentId: order.OrderParentId,
        isExpanded: false,
      }));
      setOrderDetails(newOrderDetails);
    }
  }, [allOrders, selectedTab]);

  const onTabChange = (key: number, tabName: string) => {
    setSearchValue("");
    setDebouncedSearchValue("");
    handleBreadcrumb(key);
    setToggleFilterState(false);
    setFilterOptions({
      all: false,
      active: false,
      estimate: false,
      draft: false,
    });
    navigate(`/my-workspace/${tabName}`);
  };

  const toggleFilter = () => {
    setToggleFilterState(!toggleFilterState);
  };

  const handleShareEstimateSuccess = () => {
    fetchOrders("estimate");
    setToastMessage("Estimate has been successfully shared.");
    setToastVariant("success");
  };

  const orderStatusOptions: { value: FilterOption; label: string }[] = [
    { value: "all", label: "All" },
    { value: "active", label: "My Orders" },
    { value: "estimate", label: "My Estimates" },
    { value: "draft", label: "My Drafts" },
  ];

  const getFormattedStatus = (status: string) => {
    switch (status) {
      case "order_estimate":
        return (
          <div className="my-estimates">
            <span className="indicator-icon"></span>
            <span>My Estimates</span>
          </div>
        );
      case "order_draft":
        return (
          <div className="my-drafts">
            <span className="indicator-icon"></span>
            <span>My Drafts</span>
          </div>
        );
      case "order_active":
      case "order_submitted":
        return (
          <div className="my-orders">
            <span className="indicator-icon"></span>
            <span>My Orders</span>
          </div>
        );
      default:
        return <div>Unknown Status</div>;
    }
  };

  const getOrderAction = (
    order: any,
    disabled?: boolean,
    selectedTab?: number,
  ) => {
    const status = order?.StatusCd;
    const orderId = order?.OrderParentId;
    switch (status) {
      case "order_estimate":
        return (
          <div className="order-action-buttons">
            {disabled ? (
              <>
                <MotifButton
                  className="order-action-button"
                  onClick={() => {
                    navigate(`/checkout/${orderId}`);
                  }}
                >
                  View
                </MotifButton>
              </>
            ) : (
              <>
                <MotifButton
                  className="order-action-button"
                  onClick={() => {
                    setOrderId(orderId);
                    navigate("/checkout");
                  }}
                >
                  Add to Cart
                </MotifButton>
                <MotifDropdown
                  className="order-action-dropdown"
                  placement="auto"
                  trigger={
                    <MotifButton
                      id="dropdown-trigger-1"
                      type="button"
                      className="order-action-button"
                      disabled={disabled}
                    >
                      ...
                    </MotifButton>
                  }
                >
                  <MotifDropdownItem
                    onClick={() => {
                      setOrderId(orderId);
                      navigate("/checkout");
                    }}
                  >
                    <MotifIcon
                      iconFunction={IconoirEditPencil}
                      size="24"
                      title="Edit"
                    />
                    Edit
                  </MotifDropdownItem>
                  {selectedTab === 2 && (
                    <MotifDropdownItem
                      onClick={() => {
                        setCurrentSharedOrder(order);
                        setShowShareModal(true);
                      }}
                    >
                      <MotifIcon
                        iconFunction={IconoirShareIos}
                        size="24"
                        title="Share"
                        className="share-btn"
                      />
                      Share
                    </MotifDropdownItem>
                  )}
                  <MotifDropdownItem
                    onClick={() => {
                      setCurrentOrderId(orderId);
                      setShowDeleteOrderModel(true);
                    }}
                  >
                    <MotifIcon
                      iconFunction={IconoirTrash}
                      size="24"
                      title="Delete"
                    />
                    Delete
                  </MotifDropdownItem>
                </MotifDropdown>
              </>
            )}
          </div>
        );
      case "order_draft":
        return (
          <div className="order-action-buttons">
            <MotifButton
              className="order-action-button"
              onClick={() => {
                setOrderId(orderId);
                navigate("/checkout");
              }}
            >
              Add to Cart
            </MotifButton>
            <MotifDropdown
              className="order-action-dropdown"
              placement="auto"
              trigger={
                <MotifButton
                  id="dropdown-trigger-1"
                  type="button"
                  className="order-action-button"
                >
                  ...
                </MotifButton>
              }
            >
              <MotifDropdownItem
                onClick={() => {
                  setOrderId(orderId);
                  navigate("/checkout");
                }}
              >
                Edit Order Bundle
              </MotifDropdownItem>
              <MotifDropdownItem
                onClick={() => {
                  setCurrentOrderId(orderId);
                  setShowDeleteOrderModel(true);
                }}
              >
                Delete Order Bundle
              </MotifDropdownItem>
            </MotifDropdown>
          </div>
        );
      case "order_active":
        return <div className="order-action-txt">N/A</div>;
      case "order_submitted":
        return <div className="order-action-txt">N/A</div>;
      default:
        return "Unknown Action";
    }
  };

  const getSelectedFilterLabels = (): string[] =>
    orderStatusOptions
      .filter((option) => filterOptions[option.value])
      .map((option) => option.label);

  const renderNoSearchResult = () => {
    const selectedFilters = getSelectedFilterLabels();

    if (selectedFilters.length > 0) {
      const header = searchValue
        ? `No result under the filter and by the keyword "${searchValue}"`
        : `No result under the filter`;

      const message = searchValue
        ? "Did you not find any results? We recommend refining your search criteria and/or broadening your filter parameters to enhance your search outcomes."
        : "Did not find any results? We recommend broadening your filter criteria for a more comprehensive search.";

      return (
        <NoSearchResult
          searchValue={searchValue}
          header={header}
          message={message}
        />
      );
    } else {
      return <NoSearchResult searchValue={searchValue} />;
    }
  };

  const orderBundleColumns = [
    {
      key: "OrderName",
      label: "Order Name/ID",
      width: "25%",
      render: (order: any) => (
        <div className="sku-name-responsive">
          <MotifTruncate maxLines={1} className="sku-truncate-responsive">
            {order.Description}
          </MotifTruncate>
          <div className="order-id">{order.OrderParentId}</div>
        </div>
      ),
    },
    {
      key: "Total",
      label: "Total Cost of Bundle",
      width: "15%",
      render: (order: any) => (
        <div className="order-total">{getTotalOrderAmount(order)}</div>
      ),
    },
    {
      key: "CreatedDtm",
      label: "Date Created",
      width: "16%",
      render: (order: any) => (
        <div className="order-date">{getFormattedDate(order.CreatedDtm)}</div>
      ),
    },
    {
      key: "View",
      label: "View Order Items",
      width: "16%",
      render: (order: any) => {
        const isExpanded =
          getArrowDirection(order.OrderParentId, orderDetails) === "up";
        return (
          <div className="view-order">
            <div
              className="view-order-items"
              onClick={() =>
                setOrderDetails(
                  onViewOrderItems(order.OrderParentId, orderDetails),
                )
              }
            >
              <div>{isExpanded ? "Close Order Items" : "View Order Items"}</div>
              <div>
                {isExpanded ? (
                  <MotifIcon iconFunction={IconoirNavArrowUp} size="20" />
                ) : (
                  <MotifIcon iconFunction={IconoirNavArrowDown} size="20" />
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: "StatusCd",
      label: "Status",
      width: "14%",
      render: (order: any) => (
        <div className="order-status">{getFormattedStatus(order.StatusCd)}</div>
      ),
    },
    {
      key: "Action",
      label: "Action",
      width: "14%",
      render: (order: any) => {
        return (
          <div className="order-action">
            {getOrderAction(order, false, selectedTab)}
          </div>
        );
      },
    },
  ];

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleSyncOrder = (orderId: number, status?: string) => {
    resetSyncOrderState();
    dispatch(syncOrder({ OrderId: Number(orderId) }))
      .then((response: any) => {
        if (response.payload?.Status === "success") {
          setToastMessage(
            response.payload?.Message || "Order synced to CRM successfully.",
          );
          setToastVariant("success");
        } else {
          setToastMessage(
            response.payload?.Message ||
              "Failed to sync order. Please try again.",
          );
          setToastVariant("error");
        }
        fetchOrders(status); // Refetch the orders after syncing to update the UI
      })
      .catch((error: any) => {
        console.error("Error syncing order:", error);
      });
  };

  return (
    <div className="home-workspace-section">
      <div className="toast-container">
        {toastMessage && (
          <MotifToast
            onClose={() => setToastMessage("")}
            variant={toastVariant}
            position="top"
          >
            <div slot="toast-supporting-text">{toastMessage}</div>
          </MotifToast>
        )}
      </div>
      <section className="workspace-container">
        <div className="workspace-tab-container">
          <MotifTabNavigation
            defaultActiveKey={selectedTab}
            orientation="horizontal"
            size="md"
            variant="default"
            controlled={true}
          >
            <MotifTabControl
              onClick={() => onTabChange(0, "all-order-bundles")}
              className={`motif-tab-button ${
                selectedTab === 0 ? "motif-active" : ""
              }`}
            >
              All Order Bundles
            </MotifTabControl>
            <MotifTabControl
              onClick={() => onTabChange(1, "my-orders")}
              className={`motif-tab-button ${
                selectedTab === 1 ? "motif-active" : ""
              }`}
            >
              My Orders
            </MotifTabControl>
            <MotifTabControl
              onClick={() => onTabChange(2, "my-estimates")}
              className={`motif-tab-button ${
                selectedTab === 2 ? "motif-active" : ""
              }`}
            >
              My Estimates
            </MotifTabControl>
            <MotifTabControl
              onClick={() => onTabChange(3, "my-drafts")}
              className={`motif-tab-button ${
                selectedTab === 3 ? "motif-active" : ""
              }`}
            >
              My Drafts
            </MotifTabControl>
          </MotifTabNavigation>
          <div className="myworkspace-search-container">
            <div className="filter-button-wrapper">
              <MotifButton
                aria-label="filter"
                id="dropdown-trigger-1"
                className="myworkspace-filter"
                onClick={toggleFilter}
              >
                Filter
                <MotifIcon
                  size={"20"}
                  iconFunction={IconoirFilterList}
                  className="motif-filter-icon"
                />
              </MotifButton>

              {selectedTab === 0 && toggleFilterState && (
                <div className="filter-container">
                  {orderStatusOptions.map((option) => (
                    <MotifCheckbox
                      key={option.value}
                      id={`select-${option.value}`}
                      name={`select-${option.value}`}
                      onChange={() => handleFilterChange(option.value)}
                      checked={filterOptions[option.value]}
                      value={option.value}
                      className="status-select-checkbox"
                    >
                      {option.label}
                    </MotifCheckbox>
                  ))}
                </div>
              )}
            </div>

            {/* Other elements like search input go here */}
            <div>
              <SearchInput
                id={"my-workspace-search-details"}
                aria-label={"Search"}
                onChange={(e: any) => {
                  const isContainEmoji = containsEmoji(e.target.value);
                  if (isContainEmoji) {
                    setSearchValidationError(EMOJI_ERROR_MESSAGE);
                  } else {
                    setSearchValidationError("");
                  }
                  setSearchValue(e.target.value);
                  if (e.target.value === "") {
                    handleSearch("");
                  }
                }}
                clearSearch={() => {
                  handleSearch("");
                  setSearchValidationError("");
                }}
                onEnter={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  const isContainEmoji = containsEmoji(e.currentTarget.value);
                  if (!isContainEmoji) {
                    handleSearch(e.currentTarget.value);
                  }
                }}
                placeholder={"Search"}
                value={searchValue}
                className="my-workspace-product-search"
              ></SearchInput>
              {searchValidationError && (
                <MotifErrorMessage className="search-helper-text">
                  {searchValidationError}
                </MotifErrorMessage>
              )}
            </div>
          </div>
        </div>
        <div className="workspace-orders-section-wrapper">
          {selectedTab === 0 && (
            <>
              {isLoading && (
                <MotifProgressLoader
                  aria-busy="true"
                  aria-label="This page is loading"
                  aria-live="polite"
                  arialabel="This page is loading"
                  show
                  variant="default"
                  className="home-workspace-progress"
                />
              )}
              {!isLoading && (
                <>
                  <div className="workspace-orders-section">
                    <div className="workspace-orders-list">
                      <div className="workspace-orders-header">
                        <h3 className="workspace-orders-title">
                          All Order Bundles
                        </h3>
                        <p className="workspace-orders-description">
                          Products and services you're currently using across
                          your engagements
                        </p>
                      </div>
                      <div className="workspace-orders-content">
                        {Array.isArray(allOrders) && allOrders?.length == 0 ? (
                          <>
                            {isError && (
                              <ErrorOrderPage
                                image={productListEmptyImg}
                                header="An error has occurred while attempting to retrieve data. Please choose the option to refresh the page."
                                handleRefresh={() => {
                                  fetchOrders();
                                }}
                              />
                            )}
                            {!isError && (
                              <>
                                {searchValue == "" &&
                                selectedFiltersValues.length == 0 ? (
                                  <div className=" home-empty-workspace-default">
                                    <div>
                                      <img
                                        src={productListEmptyImg}
                                        alt="Empty Product List"
                                      />
                                    </div>
                                    <div className="empty-workspace-header">
                                      No Order Bundles Found
                                    </div>
                                    <div className="empty-workspace-sub-header">
                                      It looks like you do not have any order
                                      bundles yet. Please browse our technology
                                      products to start adding items to your
                                      order bundles.
                                    </div>
                                    <div className="empty-workspace-button-wrapper">
                                      <MotifButton
                                        className="empty-workspace-button"
                                        onClick={goBackToCTTab}
                                      >
                                        Browse Technology Products
                                      </MotifButton>
                                    </div>
                                  </div>
                                ) : (
                                  renderNoSearchResult()
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          <OrdersTable
                            data={allOrders}
                            orderDetails={orderDetails}
                            columnsData={orderBundleColumns}
                            showEngagementColumn={true}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {selectedTab === 1 && (
            <MyOrders
              activeOrders={activeOrderData}
              isLoadingActive={isLoadingActive}
              searchText={searchValue}
              isActiveError={isActiveError}
              refreshCallback={() => fetchOrders("active")}
              handleSyncOrder={handleSyncOrder}
            />
          )}
          {selectedTab === 2 && (
            <>
              <MyEstimates
                estimatedOrders={estimatedOrderData}
                isLoadingEstimates={isLoadingEstimate}
                getOrderAction={getOrderAction}
                searchText={searchValue}
                isEstimateError={isEstimateError}
                refreshCallback={() => fetchOrders("estimate")}
                handleSyncOrder={handleSyncOrder}
              />
              {showShareModal && (
                <ShareEstimateModal
                  show={showShareModal}
                  setShow={setShowShareModal}
                  sharedOrder={currentSharedOrder}
                  handleShareEstimateSuccess={handleShareEstimateSuccess}
                />
              )}
            </>
          )}
          {selectedTab === 3 && (
            <MyDrafts
              draftOrders={draftOrderData}
              isLoadingDrafts={isLoadingDrafts}
              getOrderAction={getOrderAction}
              searchText={searchValue}
              isDraftError={isDraftError}
              refreshCallback={() => fetchOrders("draft")}
            />
          )}
        </div>
      </section>
      <DeleteOrderModal
        isVisible={showDeleteOrderModel}
        popupCloseHandler={() => {
          setShowDeleteOrderModel(false);
          fetchMyWorkspaceData();
        }}
        orderId={"" + currentOrderId}
      />
    </div>
  );
};

export default MyWorkspace;
